/**
 * Contact Modal — Help & Contact form
 * Sends user messages to help@knods.io via Resend
 */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './ContactModal.css';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES = [
  { value: 'General', label: 'General Question' },
  { value: 'Bug Report', label: 'Bug Report' },
  { value: 'Feature Request', label: 'Feature Request' },
  { value: 'Billing', label: 'Billing & Credits' },
];

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [category, setCategory] = useState('General');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user?.id) headers['x-user-id'] = user.id;

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          category,
          subject: subject.trim(),
          message: message.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSuccess(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCategory('General');
    setSubject('');
    setMessage('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="contact-overlay" onClick={handleClose}>
      <div className="contact-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="contact-header">
          <div className="header-title">
            <HelpOutlineIcon />
            Help & Contact
          </div>
          <button className="close-btn" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Content */}
        <div className="contact-content">
          {success ? (
            <div className="contact-success">
              <CheckCircleIcon className="success-icon" />
              <h3>Message sent!</h3>
              <p>We'll get back to you at <strong>{user?.email}</strong> as soon as possible.</p>
              <button className="done-btn" onClick={handleClose}>Done</button>
            </div>
          ) : (
            <>
              <p className="contact-intro">
                We'd love to hear from you. Tell us what's on your mind and we'll get back to you shortly.
              </p>

              {/* Category */}
              <div className="contact-field">
                <label>Category</label>
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  disabled={loading}
                >
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div className="contact-field">
                <label>Subject <span className="optional">(optional)</span></label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Brief summary"
                  disabled={loading}
                  maxLength={200}
                />
              </div>

              {/* Message */}
              <div className="contact-field">
                <label>Message</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Describe what you need help with..."
                  disabled={loading}
                  rows={5}
                  maxLength={5000}
                />
              </div>

              {error && <div className="contact-error">{error}</div>}

              <button
                className="contact-send-btn"
                onClick={handleSubmit}
                disabled={loading || !message.trim()}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>

              <p className="contact-alt">
                Or email us directly at <a href="mailto:help@knods.io">help@knods.io</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContactModal;
